import { useCallback, useEffect, useState } from 'react';
import { ApiFailure, api, describeError } from '../api';
import { FileButton, Json, Message, Pill, Section } from '../components/ui';
import { useSession } from '../session';

interface ChatSummary { id: string; name: string; character_id: string | null }
interface Character { id: string; name: string }
interface Summary { text: string | null; last_seq: number | null; updated_at: string | null; frozen: boolean; source: string | null; auto: boolean; pending_messages: number; last_error: string | null }
interface Vectors { enabled: boolean; indexed_messages: number; indexed_chunks: number; pending_jobs: number; quota_exceeded: boolean }
interface FileRow { id: string; scope: 'global' | 'character' | 'chat'; scope_id: string | null; name: string; bytes: number; status: string; chunks: number; inline: boolean; created_at: string; indexed_at: string | null; text?: string }
interface ChatMemory { chat_id: string; enabled: boolean; platform_enabled: boolean; summary: Summary; vectors: Vectors; data_bank: { files: FileRow[]; enabled: boolean }; world_info: { enabled: boolean; indexed_entries: number } }
interface Receipt { usage: Record<string, number | undefined>; credits_charged: number | null; balance_after: number | null; model: string; channel: string; charged: boolean }
interface Entitlements { limits: { data_bank_bytes: { limit: number | null; count: number } } }
interface SettingsDoc { version: number; json: Record<string, unknown> }

const SCOPE_LABEL: Record<FileRow['scope'], string> = { global: '全局', character: '角色', chat: '会话' };

export function ChatMemoryPanel({ chatId, characterId, itemized, activationTrace }: { chatId: string | null; characterId?: string | null; itemized?: Array<{ source: string }>; activationTrace?: Array<{ reason: string; book: string; uid: number; comment: string; activated: boolean }> }) {
  const { me } = useSession();
  const [mem, setMem] = useState<ChatMemory | null>(null);
  const [draft, setDraft] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!me || !chatId) return;
    try {
      const { data } = await api<ChatMemory>(`/api/chats/${chatId}/memory`);
      setMem(data);
      setDraft(data.summary.text ?? '');
    } catch (e) { setErr(describeError(e)); }
  }, [me, chatId]);
  useEffect(() => { void load(); }, [load]);

  const run = async (fn: () => Promise<string | void>) => {
    if (!chatId) return;
    setErr(null); setMsg(null); setBusy(true);
    try { const m = await fn(); if (m) setMsg(m); await load(); } catch (e) { setErr(describeError(e)); } finally { setBusy(false); }
  };

  const recalled = (itemized ?? []).filter((s) => s.source === 'injection:3_vectors' || s.source === 'injection:4_vectors_data_bank' || s.source === 'injection:1_memory');
  const external = (activationTrace ?? []).filter((t) => t.reason === 'external');

  if (!chatId) return <Section title="记忆"><div className="text-xs text-zinc-500">选择一个会话。</div></Section>;
  return (
    <Section title="记忆（change 15）" actions={<button className="btn-ghost" onClick={() => void load()}>刷新</button>}>
      <Message text={err} kind="error" />
      <Message text={msg} kind="ok" />
      {!mem?.platform_enabled && <Message text="平台熔断 feature_flags.memory_enabled = false：不注入、不入队" kind="error" />}
      {mem && (
        <div className="space-y-2 text-xs">
          <div className="flex flex-wrap gap-1">
            <Pill tone={mem.enabled ? 'emerald' : 'rose'}>会话 {mem.enabled ? '开' : '关'}</Pill>
            <Pill tone={mem.summary.frozen ? 'amber' : 'zinc'}>{mem.summary.frozen ? '已冻结' : '未冻结'}</Pill>
            <Pill>自动 {mem.summary.auto ? `每间隔 · 待 ${mem.summary.pending_messages}` : '关'}</Pill>
            <Pill tone={mem.vectors.enabled ? 'emerald' : 'zinc'}>向量 {mem.vectors.indexed_messages} 条 / {mem.vectors.indexed_chunks} 块{mem.vectors.pending_jobs ? ` · 待 ${mem.vectors.pending_jobs}` : ''}</Pill>
            {mem.vectors.quota_exceeded && <Pill tone="rose">块数上限</Pill>}
            <Pill>WI 语义 {mem.world_info.enabled ? `${mem.world_info.indexed_entries} 条` : '关'}</Pill>
          </div>
          <textarea className="input text-xs" rows={4} value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="总结文本（空 = 清空）" />
          <div className="flex flex-wrap gap-1">
            <button className="btn" disabled={busy} onClick={() => void run(async () => { await api(`/api/chats/${chatId}/memory`, { method: 'PATCH', body: { summary: draft.trim() || null } }); return '已保存总结'; })}>保存总结</button>
            <button className="btn-ghost" disabled={busy} onClick={() => void run(async () => { const { data } = await api<{ skipped: string | null; receipt: Receipt | null }>(`/api/chats/${chatId}/memory/summarize`, { method: 'POST', body: { force: true } }); return data.skipped ? `skipped ${data.skipped}` : `已总结${data.receipt?.charged ? ` · 扣 ${data.receipt.credits_charged} 钻石` : ''}`; })}>立即总结</button>
            <button className="btn-ghost" disabled={busy} onClick={() => void run(async () => { await api(`/api/chats/${chatId}/memory`, { method: 'PATCH', body: { frozen: !mem.summary.frozen } }); return mem.summary.frozen ? '已解冻' : '已冻结'; })}>{mem.summary.frozen ? '解冻' : '冻结'}</button>
            <button className="btn-ghost" disabled={busy} onClick={() => void run(async () => { await api(`/api/chats/${chatId}/memory`, { method: 'PATCH', body: { enabled: !mem.enabled } }); return mem.enabled ? '已关闭本会话记忆' : '已开启'; })}>{mem.enabled ? '关闭本会话' : '开启本会话'}</button>
            <button className="btn-ghost text-rose-300" disabled={busy} onClick={() => void run(async () => { const { data } = await api<{ deleted: number }>(`/api/chats/${chatId}/memory/vectors`, { method: 'DELETE' }); return `已清空 ${data.deleted} 块`; })}>清空向量</button>
          </div>
          {mem.summary.last_error && <div className="text-amber-200">last_error: {mem.summary.last_error}</div>}
          {recalled.length > 0 && <div className="text-zinc-400">本轮 itemized：{recalled.map((s) => s.source).join(' · ')}</div>}
          {external.length > 0 && <div className="text-emerald-300">external 激活：{external.map((t) => `${t.book}#${t.uid}`).join(' · ')}</div>}
          {characterId && <div className="text-zinc-500">角色 {characterId.slice(0, 8)}… · 可见 Data Bank {mem.data_bank.files.length} 个</div>}
        </div>
      )}
    </Section>
  );
}

export function MemoryPage() {
  const { env, me } = useSession();
  const [chats, setChats] = useState<ChatSummary[]>([]);
  const [characters, setCharacters] = useState<Character[]>([]);
  const [chatId, setChatId] = useState('');
  const [files, setFiles] = useState<FileRow[]>([]);
  const [scope, setScope] = useState<FileRow['scope']>('global');
  const [scopeId, setScopeId] = useState('');
  const [name, setName] = useState('notes.md');
  const [text, setText] = useState('');
  const [used, setUsed] = useState<{ limit: number | null; count: number } | null>(null);
  const [ext, setExt] = useState<SettingsDoc | null>(null);
  const [interval, setIntervalVal] = useState(10);
  const [enabledChats, setEnabledChats] = useState(false);
  const [enabledFiles, setEnabledFiles] = useState(true);
  const [enabledWi, setEnabledWi] = useState(false);
  const [protect, setProtect] = useState(5);
  const [insert, setInsert] = useState(3);
  const [query, setQuery] = useState(2);
  const [threshold, setThreshold] = useState(0.25);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!me) return;
    try {
      const [c, ch, f, e, s] = await Promise.all([
        api<ChatSummary[]>('/api/chats?per_page=50'),
        api<Character[]>('/api/characters?fields=summary&per_page=50'),
        api<FileRow[]>('/api/data-bank/files'),
        api<Entitlements>('/api/me/entitlements'),
        api<SettingsDoc>('/api/me/settings/extension_settings'),
      ]);
      setChats(c.data);
      setCharacters(ch.data);
      setFiles(f.data);
      setUsed(e.data.limits.data_bank_bytes);
      setExt(s.data);
      const mem = (s.data.json.memory ?? {}) as Record<string, unknown>;
      const vec = (s.data.json.vectors ?? {}) as Record<string, unknown>;
      if (typeof mem.promptInterval === 'number') setIntervalVal(mem.promptInterval);
      if (typeof vec.enabled_chats === 'boolean') setEnabledChats(vec.enabled_chats);
      if (typeof vec.enabled_files === 'boolean') setEnabledFiles(vec.enabled_files);
      if (typeof vec.enabled_world_info === 'boolean') setEnabledWi(vec.enabled_world_info);
      if (typeof vec.protect === 'number') setProtect(vec.protect);
      if (typeof vec.insert === 'number') setInsert(vec.insert);
      if (typeof vec.query === 'number') setQuery(vec.query);
      if (typeof vec.score_threshold === 'number') setThreshold(vec.score_threshold);
      if (!chatId && c.data[0]) setChatId(c.data[0].id);
    } catch (e) { setErr(describeError(e)); }
  }, [me, chatId]);
  useEffect(() => { void load(); }, [load, env]);

  const run = async (fn: () => Promise<string | void>) => {
    setErr(null); setMsg(null);
    try { const m = await fn(); if (m) setMsg(m); await load(); } catch (e) { setErr(describeError(e)); }
  };

  const saveSettings = () => run(async () => {
    if (!ext) return;
    await api('/api/me/settings/extension_settings', {
      method: 'PATCH',
      headers: { 'If-Match': `"${ext.version}"` },
      body: { memory: { promptInterval: interval }, vectors: { enabled_chats: enabledChats, enabled_files: enabledFiles, enabled_world_info: enabledWi, protect, insert, query, score_threshold: threshold } },
    });
    return '已写入 extension_settings.memory / vectors';
  });

  const upload = () => run(async () => {
    if (!text.trim()) return;
    const body: Record<string, unknown> = { scope, name: name.trim() || 'notes.md', text };
    if (scope !== 'global') body.scope_id = scopeId;
    const { data } = await api<FileRow>('/api/data-bank/files', { method: 'POST', body });
    setText('');
    return `已上传 ${data.name}（${data.status}${data.inline ? ' · 整文' : ''}）`;
  });

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <div className="space-y-4">
        <Section title="设置 · extension_settings.memory / vectors">
          {!me && <Message text="请先登录" />}
          <Message text={err} kind="error" />
          <Message text={msg} kind="ok" />
          <div className="grid grid-cols-2 gap-2 text-xs">
            <label className="flex items-center gap-2">promptInterval<input className="input w-20" type="number" min={0} value={interval} onChange={(e) => setIntervalVal(Number(e.target.value))} /></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={enabledChats} onChange={(e) => setEnabledChats(e.target.checked)} />enabled_chats</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={enabledFiles} onChange={(e) => setEnabledFiles(e.target.checked)} />enabled_files</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={enabledWi} onChange={(e) => setEnabledWi(e.target.checked)} />enabled_world_info</label>
            <label className="flex items-center gap-2">protect<input className="input w-16" type="number" min={0} value={protect} onChange={(e) => setProtect(Number(e.target.value))} /></label>
            <label className="flex items-center gap-2">insert<input className="input w-16" type="number" min={1} value={insert} onChange={(e) => setInsert(Number(e.target.value))} /></label>
            <label className="flex items-center gap-2">query<input className="input w-16" type="number" min={1} value={query} onChange={(e) => setQuery(Number(e.target.value))} /></label>
            <label className="flex items-center gap-2">threshold<input className="input w-20" type="number" min={0} max={1} step={0.05} value={threshold} onChange={(e) => setThreshold(Number(e.target.value))} /></label>
          </div>
          <button className="btn" disabled={!ext} onClick={() => void saveSettings()}>写入设置</button>
          <p className="text-[11px] text-zinc-500">ST 默认：会话向量关、Data Bank 开、WI 语义关。自动总结默认每 10 条。MVP 客户端不露此页，console 用来验收。</p>
        </Section>
        <ChatMemoryPanel chatId={chatId || null} characterId={chats.find((c) => c.id === chatId)?.character_id} />
        <Section title="选择会话">
          <select className="input" value={chatId} onChange={(e) => setChatId(e.target.value)}>{chats.map((c) => <option key={c.id} value={c.id}>{c.name || c.id.slice(0, 8)}</option>)}</select>
        </Section>
      </div>
      <Section
        title={`Data Bank · ${files.length}`}
        actions={used && <Pill>{used.count} / {used.limit ?? '∞'} B</Pill>}
      >
        <div className="grid grid-cols-2 gap-2 text-xs">
          <select className="input" value={scope} onChange={(e) => setScope(e.target.value as FileRow['scope'])}>
            <option value="global">global</option>
            <option value="character">character</option>
            <option value="chat">chat</option>
          </select>
          {scope === 'character' && <select className="input" value={scopeId} onChange={(e) => setScopeId(e.target.value)}>{characters.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select>}
          {scope === 'chat' && <select className="input" value={scopeId} onChange={(e) => setScopeId(e.target.value)}>{chats.map((c) => <option key={c.id} value={c.id}>{c.name || c.id.slice(0, 8)}</option>)}</select>}
          <input className="input col-span-2" value={name} onChange={(e) => setName(e.target.value)} placeholder="文件名.md" />
        </div>
        <textarea className="input font-mono text-[11px]" rows={6} value={text} onChange={(e) => setText(e.target.value)} placeholder="纯文本 / Markdown" />
        <div className="flex gap-2">
          <button className="btn" disabled={!text.trim() || (scope !== 'global' && !scopeId)} onClick={() => void upload()}>上传</button>
          <FileButton label="从 .txt / .md" accept=".txt,.md,.markdown" onFile={(file) => void (async () => { setName(file.name); setText(await file.text()); })()} />
        </div>
        <ul className="divide-y divide-zinc-800 text-xs">
          {files.map((f) => (
            <li key={f.id} className="flex items-center justify-between gap-2 py-2">
              <div>
                <div>{f.name} <Pill>{SCOPE_LABEL[f.scope]}</Pill> <Pill tone={f.status === 'ready' ? 'emerald' : f.status === 'failed' ? 'rose' : 'amber'}>{f.status}</Pill>{f.inline && <Pill>整文</Pill>}</div>
                <div className="text-zinc-500">{f.bytes} B · {f.chunks} 块 · {f.id.slice(0, 8)}</div>
              </div>
              <div className="flex gap-1">
                <button className="btn-ghost py-0" onClick={() => void run(async () => { await api(`/api/data-bank/files/${f.id}/reindex`, { method: 'POST' }); return '已 reindex'; })}>重建</button>
                <button className="btn-ghost py-0 text-rose-300" onClick={() => void run(async () => { await api(`/api/data-bank/files/${f.id}`, { method: 'DELETE' }); return '已删除'; })}>删</button>
              </div>
            </li>
          ))}
        </ul>
        {files[0] && <details className="text-[11px] text-zinc-500"><summary>最近文件 JSON</summary><Json value={files[0]} /></details>}
      </Section>
    </div>
  );
}
